import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useNotify, usePermissions } from "react-admin";

import { SelfLeaveCreateDialog } from "@/containers/staff/leave-requests/SelfLeaveCreateDialog";
import {
  leaveHistoryCreateHref,
  leaveHistoryShowPath,
  leaveListApiLocation,
  leaveStatusChipColor,
  leaveTypeLabel,
  formatLeaveApproverRemarks,
  isDecidedLeaveStatus,
  truncateLeaveRemarks,
  type LeaveHistoryMode,
} from "@/containers/staff/leave-requests/leave-request-helpers";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { formatListDate } from "@/utils/date-format";

type LeaveRow = {
  id: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
  source: string;
  reason?: string | null;
  reviewRemarks?: string | null;
  reviewedAt?: string | null;
};

type Props = {
  employeeId: number;
  /** Office staff dashboard vs personal My dashboard. Default office. */
  mode?: LeaveHistoryMode;
};

/** Leave history for one employee (office) or the linked self (personal hub). */
export function StaffLeaveHistorySection({ employeeId, mode = "office" }: Props) {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const isSelf = mode === "self";
  const canCreateOffice = hasPermission(permissions, "employees.create");
  const canCreate = isSelf || canCreateOffice;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<LeaveRow[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchLeaves = useCallback(async () => {
    if (!isSelf && !employeeId) return;
    setLoading(true);
    try {
      const { data: response } = await callApi({
        location: leaveListApiLocation(mode),
        method: "GET",
        data: isSelf
          ? { limit: 50, orderBy: "createdAt", order: "DESC" }
          : { employeeId, limit: 50, orderBy: "createdAt", order: "DESC" },
      });
      const body = response as { rows?: LeaveRow[] };
      setRows(Array.isArray(body?.rows) ? body.rows : []);
    } catch {
      notify("Failed to load leave requests", { type: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, isSelf, mode, notify]);

  useEffect(() => {
    void fetchLeaves();
  }, [fetchLeaves]);

  const createHref = leaveHistoryCreateHref(mode, employeeId);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2">{isSelf ? "My leave" : "Leave requests"}</Typography>
        <Stack direction="row" spacing={1}>
          {!isSelf ? (
            <Button component={RouterLink} to="/employeeLeaveRequests" size="small" variant="text">
              View all
            </Button>
          ) : null}
          {canCreate ? (
            createHref ? (
              <Button component={RouterLink} to={createHref} size="small" variant="outlined">
                New leave
              </Button>
            ) : (
              <Button size="small" variant="outlined" onClick={() => setCreateOpen(true)}>
                New leave
              </Button>
            )
          ) : null}
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No leave requests yet. Approved leave appears as L and pending as Lp on the attendance
          calendar.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                Approver remarks
              </TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const showTo = leaveHistoryShowPath(mode, row.id);
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <MuiLink component={RouterLink} to={showTo} underline="hover" color="inherit">
                      {leaveTypeLabel(row.leaveType)}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{formatListDate(row.startDate, { empty: "—" })}</TableCell>
                  <TableCell>{formatListDate(row.endDate, { empty: "—" })}</TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        label={row.status}
                        size="small"
                        color={leaveStatusChipColor(row.status)}
                        component={RouterLink}
                        to={showTo}
                        clickable
                      />
                      {isDecidedLeaveStatus(row.status) && row.reviewRemarks?.trim() ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: { sm: "none" }, mt: 0.5 }}
                        >
                          {formatLeaveApproverRemarks(row.reviewRemarks)}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    {isDecidedLeaveStatus(row.status) && row.reviewRemarks?.trim()
                      ? truncateLeaveRemarks(row.reviewRemarks)
                      : "—"}
                  </TableCell>
                  <TableCell>{row.source}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {isSelf ? (
        <SelfLeaveCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => void fetchLeaves()}
        />
      ) : null}
    </Box>
  );
}
